import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuDownload, LuFile, LuFileText, LuTag } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useSessionHold from '../../../hooks/useSessionHold';
import { useTranslation } from '../../../i18n/LanguageContext';
import { EmptyState } from '../components/ProfileUi';
import ReadConfirmation from './ReadConfirmation';
import WordDocumentView from './WordDocumentView';

// Okumaya fırsat tanımak için belge yüklendikten bu kadar sonra onay açılır (SOP'lerle aynı)
const READ_DELAY_MS = 3000;

const IFRAME_CLASS = 'tw-block tw-h-[min(70vh,720px)] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white dark:tw-border-white/10';

/**
 * Politika / görev tanımı görüntüleyici: PDF doğrudan, .docx sayfanın içinde
 * (WordDocumentView), eski .doc Google'ın görüntüleyicisiyle açılır. Sonunda
 * "Okudum ve anladım" onayı: belge yüklendikten birkaç saniye sonra (ya da
 * önizlenemeyen dosya indirilince) açılır; okunmuşken dokunmak geri alır.
 */
const AssignedDocumentViewer = ({ kind, docItem, docKind, title, isRead, onToggleRead, onDownload, onClose }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.${key}`, vars);
  const [canConfirm, setCanConfirm] = useState(false);
  // Belge iframe'de okunurken bu sayfaya olay gelmez; açık kaldıkça çıkışı uzat
  useSessionHold(Boolean(docItem));

  const unlockTimer = useRef(null);

  // Başka belgeye geçince (ya da kapanınca) kilit yeniden kurulur; önceki
  // belgenin bekleyen sayacı yenisinin kutusunu açmasın
  useEffect(() => {
    setCanConfirm(false);
    return () => window.clearTimeout(unlockTimer.current);
  }, [docItem?.id]);

  const unlockLater = () => {
    window.clearTimeout(unlockTimer.current);
    unlockTimer.current = window.setTimeout(() => setCanConfirm(true), READ_DELAY_MS);
  };
  const download = () => {
    onDownload();
    setCanConfirm(true);
  };

  const renderDocument = () => {
    if (!docItem.fileUrl) return <EmptyState compact icon={LuFile} title={label('noFile')} />;
    if (docKind === 'pdf') {
      return <iframe src={`${docItem.fileUrl}#navpanes=0`} title={title} onLoad={unlockLater} className={IFRAME_CLASS} />;
    }
    if (docKind === 'word') {
      return (
        <WordDocumentView
          url={docItem.fileUrl}
          title={title}
          onReady={unlockLater}
          onDownload={download}
          loadingLabel={label('assignedViewer.loading')}
          errorLabel={label('assignedViewer.renderError')}
          downloadLabel={label('download')}
        />
      );
    }
    if (docKind === 'legacyWord') {
      return (
        <iframe
          src={`https://docs.google.com/gview?url=${encodeURIComponent(docItem.fileUrl)}&embedded=true`}
          title={title}
          onLoad={unlockLater}
          className={IFRAME_CLASS}
        />
      );
    }
    return <EmptyState compact icon={LuFile} title={label('cannotPreview')} />;
  };

  return (
    <Modal
      isOpen={Boolean(docItem)}
      onClose={onClose}
      size="xl"
      title={title}
      subtitle={label(`categories.${kind}.title`)}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuFileText aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} className="max-sm:tw-hidden">{t('common.close')}</Button>
          {docItem?.fileUrl && (
            <Button variant="primary" onClick={download} className="max-sm:tw-flex-1">
              <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('download')}
            </Button>
          )}
        </>
      )}
    >
      {docItem && (
        <div className="tw-space-y-4">
          <div className="tw-space-y-1.5">
            <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              {docItem.version && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuTag aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('version', { version: docItem.version })}</span>}
              {docItem.subtitle && <span className="tw-inline">{docItem.subtitle}</span>}
              {isRead && <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.read')}</Badge>}
            </p>
            {docItem.description && <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{docItem.description}</p>}
          </div>

          {renderDocument()}

          {docItem.fileUrl && (
            <ReadConfirmation
              isRead={isRead}
              unlocked={canConfirm || isRead}
              onToggle={onToggleRead}
              confirmLabel={label('assignedViewer.confirm')}
              lockedHint={label('assignedViewer.lockedHint')}
              readHint={label('assignedViewer.readHint')}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

AssignedDocumentViewer.propTypes = {
  kind: PropTypes.oneOf(['policies', 'jobDescriptions']).isRequired,
  docItem: PropTypes.object,
  docKind: PropTypes.oneOf(['pdf', 'word', 'legacyWord', 'other']),
  title: PropTypes.string,
  isRead: PropTypes.bool,
  onToggleRead: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AssignedDocumentViewer;
